import { useState, type CSSProperties } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { api, textoUbicacion } from '../api';
import { useApi, useDemorado, VIVO } from '../lib/useApi';
import { useEstadoGuardado } from '../lib/persistencia';
import { useSesion } from '../lib/sesion';
import { Buscador, Cargando, ChipBoton, Numero, EstadoChip, PanelError, Paginador, useConfirmar, usePaginado, Vacio } from '../components/ui';
import { mensajeDeError } from '../lib/useApi';
import { abrir, fila } from '../components/movimiento';
import {
  aviso,
  botonSecundario,
  c,
  campo,
  chipBoton,
  encabezadoTabla,
  filaTabla,
  enlace,
  mono,
  tarjeta,
  subtitulo,
} from '../theme';

// La tabla en PC es la de siempre (el hueco de 16 px entre columnas son 8 + 8 de
// cada celda); en el celular `.wms-tarjetas` la vuelve una tarjeta por pallet.
const cabeza: CSSProperties = { ...encabezadoTabla, display: undefined, gap: undefined, alignItems: undefined, padding: undefined, paddingBlock: 10, textAlign: 'left', whiteSpace: 'nowrap' };
const celda: CSSProperties = { ...filaTabla, display: undefined, gap: undefined, alignItems: undefined, padding: undefined, paddingBlock: 12, verticalAlign: 'middle' };
const lado = 'px-2 first:pl-4 last:pr-4';

export default function Stock({
  onVerPallet,
  puedeUbicar,
}: {
  onVerPallet: (pallet: string) => void;
  /** El cliente no tiene Ubicar pallets: para él «Sin ubicar» es un dato, no un link. */
  puedeUbicar: boolean;
}) {
  // El cliente no entra a la ficha del pallet: es la pantalla del depósito. Sin
  // eso, el código sería un botón que lleva a un error.
  const { vistas } = useSesion();
  const puedeAbrirla = vistas.includes('frac');
  const puedeAbrirContenedor = vistas.includes('contenedor');
  // La búsqueda y el filtro sobreviven al F5: ver `lib/persistencia.ts`.
  const [texto, setTexto] = useEstadoGuardado('stock.busqueda', '');
  const q = useDemorado(texto, 250);
  const [estado, setEstado] = useEstadoGuardado('stock.estado', 'Todos');
  // Los que se fraccionan y los que salen enteros son dos stocks distintos:
  // se piden de manera distinta, así que se miran por separado.
  const [tipo, setTipo] = useEstadoGuardado<'todos' | 'fraccionamiento' | 'completo'>(
    'stock.tipo',
    'todos',
    (v) => v === 'todos' || v === 'fraccionamiento' || v === 'completo',
  );

  // Se consulta al servidor al dejar de tipear, no en cada tecla (`q`).

  const { datos, cargando, error, recargar } = useApi(() => api.stock(q, estado), [q, estado], VIVO.normal);

  /**
   * Marcar un pallet como que sale entero, o volverlo a fraccionamiento.
   *
   * Vive acá porque Stock es la única lista donde están todos los pallets: el
   * que entró por el importador de inventario inicial no pasó por Armado y no
   * tuvo dónde elegir su clase. El servidor lo rechaza si el pallet ya tiene
   * capachos encima, y ese rechazo se muestra tal cual: es la regla, no un
   * error de la pantalla.
   */
  const [cambiando, setCambiando] = useState<string | null>(null);
  const [avisoTipo, setAvisoTipo] = useState<string | null>(null);
  const [confirmar, dialogoConfirmar] = useConfirmar();

  async function alternarTipo(id: string, tipo: 'fraccionamiento' | 'completo') {
    const destino = tipo === 'completo' ? 'fraccionamiento' : 'completo';
    const aDonde = destino === 'completo' ? 'entero' : 'que se fraccione';
    if (!(await confirmar({ titulo: `¿Cambiar ${id} a ${aDonde}?`, boton: `Cambiar a ${aDonde}` }))) return;
    setCambiando(id);
    setAvisoTipo(null);
    try {
      await api.cambiarTipoPallet(id, destino);
      await recargar();
    } catch (e) {
      setAvisoTipo(mensajeDeError(e));
    } finally {
      setCambiando(null);
    }
  }

  // Ir al mapa del depósito en esa posición, o a Ubicar pallets —con ese
  // pallet ya elegido— si todavía no tiene una. Es el mismo mecanismo de `irA` en App.tsx (hash de la ruta):
  // cambiar el hash dispara su `hashchange` y no hace falta que Stock reciba
  // ninguna prop nueva de navegación.
  const irAlMapa = (posicion: string) => {
    window.location.hash = `#/racks/${encodeURIComponent(posicion)}`;
  };
  const irAContenedor = (ct: string) => {
    window.location.hash = `#/contenedor/${encodeURIComponent(ct)}`;
  };
  const irAUbicar = (pallet: string) => {
    window.location.hash = `#/ubicar/${encodeURIComponent(pallet)}`;
  };

  const limpiar = () => {
    setTexto('');
    setEstado('Todos');
    setTipo('todos');
  };

  const filtros = datos?.estados ?? ['Todos'];

  // El servidor devuelve todos los que coinciden; acá se dibujan de a página.
  const delTipo = (datos?.pallets ?? []).filter((p) => tipo === 'todos' || p.tipo === tipo);

  /**
   * Saca de su posición a todos los pallets que salen enteros, para volver a
   * ubicarlos desde Ubicar pallets. Son los de la lista (con la búsqueda y el
   * estado que estén puestos), y sólo los que tienen posición.
   */
  const enterosUbicados = (datos?.pallets ?? []).filter((p) => p.tipo === 'completo' && p.ubicado);
  // El admin lo puede apagar desde Mantenimiento. Mientras no se sabe, no se muestra.
  const ajustes = useApi(() => api.ajustes(), []);
  const sacarPrendido = ajustes.datos?.sacarEnteros === true;
  const [sacando, setSacando] = useState(false);
  async function sacarEnteros() {
    const n = enterosUbicados.length;
    const ok = await confirmar({
      titulo: `¿Sacar de su posición ${n === 1 ? 'el pallet que sale entero' : `los ${n} pallets que salen enteros`}?`,
      detalle: 'Quedan sin ubicar, con su stock intacto, para volver a ubicarlos desde Ubicar pallets. Los que se fraccionan no se tocan.',
      boton: n === 1 ? 'Sacar 1 pallet' : `Sacar ${n} pallets`,
    });
    if (!ok) return;
    setSacando(true);
    setAvisoTipo(null);
    try {
      await api.desubicarVarios(enterosUbicados.map((p) => p.id));
      await recargar();
    } catch (e) {
      setAvisoTipo(mensajeDeError(e));
    } finally {
      setSacando(false);
    }
  }
  const pallets = usePaginado(delTipo);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <div style={{ ...subtitulo, marginTop: 0 }}>
          {datos ? (
            <>
              <Numero valor={delTipo.length} desdeCero={false} /> {delTipo.length === 1 ? 'pallet' : 'pallets'}
            </>
          ) : (
            'Consultando'
          )}{' '}
          · actualización en tiempo real.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Buscador
          valor={texto}
          onCambiar={setTexto}
          placeholder="Pallet, artículo, nº de parte, lote o contenedor"
        />

        <select
          value={tipo}
          onChange={(e) => setTipo(e.target.value as typeof tipo)}
          aria-label="Qué pallets mostrar"
          style={{ ...campo, padding: '0 12px', width: 'auto' }}
        >
          <option value="todos">Todos los pallets</option>
          <option value="fraccionamiento">Los que se fraccionan</option>
          <option value="completo">Los que salen enteros</option>
        </select>

        {puedeUbicar && sacarPrendido && enterosUbicados.length > 0 && (
          <button
            type="button"
            onClick={() => void sacarEnteros()}
            disabled={sacando}
            title="Los deja sin ubicar para volver a ubicarlos: sólo los que salen enteros"
            style={{ ...botonSecundario, padding: '9px 14px', borderColor: c.ambar, color: c.ambar, opacity: sacando ? 0.6 : 1 }}
          >
            {sacando
              ? 'Sacando…'
              : `Sacar de su posición ${enterosUbicados.length === 1 ? 'el entero' : `los ${enterosUbicados.length} enteros`}`}
          </button>
        )}

        <div className="wms-tira">
          {filtros.map((f) => (
            <ChipBoton key={f} grupo="stock-filtro" activo={estado === f} onClick={() => setEstado(f)}>
              {f}
            </ChipBoton>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {avisoTipo && (
          <motion.div key={avisoTipo} {...abrir(10)} style={aviso('warn')} role="alert">
            {avisoTipo}
          </motion.div>
        )}
      </AnimatePresence>

      {error ? (
        <PanelError mensaje={error} onReintentar={recargar} />
      ) : (
        <div style={{ ...tarjeta, overflow: 'hidden' }}>
          <div className="wms-scroll-x">
            <table className="wms-tarjetas" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th className={lado} style={{ ...cabeza, minWidth: 150 }}>Pallet / artículo</th>
                  <th className={lado} style={{ ...cabeza, width: 130 }}>Nº de parte</th>
                  <th className={lado} style={{ ...cabeza, width: 110 }}>FIFO</th>
                  <th className={lado} style={{ ...cabeza, width: 90, textAlign: 'right' }}>Cantidad</th>
                  <th
                    className={lado}
                    style={{ ...cabeza, width: 100, textAlign: 'right' }}
                    title="Lo que queda en el pallet descontando también los capachos aceptados que todavía no se entregaron"
                  >
                    Remanente
                  </th>
                  <th className={lado} style={{ ...cabeza, width: 100 }}>Ubicación</th>
                  <th className={lado} style={{ ...cabeza, width: 170 }}>Estado</th>
                </tr>
              </thead>

              {cargando && !datos ? (
                <tbody>
                  <tr>
                    <td colSpan={7}>
                      <Cargando texto="Buscando pallets…" />
                    </td>
                  </tr>
                </tbody>
              ) : (
                // Al filtrar, las que quedan se deslizan a su lugar (sin `exit`: una fila que sale queda en el flujo de la tabla y las demás llegarían desde abajo);
                // al cambiar de página son otros pallets y entran de nuevo.
                <tbody>
                  {pallets.visibles.map((r, i) => (
                    <motion.tr key={r.id} layout="position" {...fila(i)}>
                      <td className={`${lado} wms-principal`} style={{ ...celda, minWidth: 150 }}>
                        <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, alignItems: 'flex-start' }}>
                          {puedeAbrirla ? (
                            <button
                              type="button"
                              onClick={() => onVerPallet(r.id)}
                              title={`Abrir ${r.id}: su contenido y todo lo que le pasó`}
                              style={{
                                border: 'none',
                                background: 'none',
                                padding: 0,
                                font: 'inherit',
                                fontWeight: 500,
                                color: c.verde,
                                textAlign: 'left',
                                cursor: 'pointer',
                              }}
                            >
                              {r.id}
                            </button>
                          ) : (
                            <span style={{ fontWeight: 500 }}>{r.id}</span>
                          )}
                          <span style={{ fontFamily: mono, fontSize: 12, color: c.texto3 }}>{r.articulo}</span>
                          {/* El contenedor va acá abajo y no en una columna
                              propia: la tabla ya está llena y esto identifica al
                              pallet, no es un dato más. */}
                          {r.contenedor &&
                            (puedeAbrirContenedor ? (
                              <button type="button" onClick={() => irAContenedor(r.contenedor!)} title="Ver la ficha del contenedor" style={{ ...enlace, fontSize: 12 }}>
                                {r.contenedor}
                              </button>
                            ) : (
                              <span style={{ fontFamily: mono, fontSize: 12, color: c.texto4 }}>{r.contenedor}</span>
                            ))}
                        </span>
                      </td>
                      <td className={lado} data-rotulo="Nº de parte" style={{ ...celda, fontFamily: mono, fontSize: 12.5, color: c.texto2 }}>
                        {r.parte}
                      </td>
                      <td className={lado} data-rotulo="FIFO" style={{ ...celda, fontFamily: mono, fontSize: 12.5, color: c.texto2 }}>
                        {r.lote}
                      </td>
                      <td className={lado} data-rotulo="Cantidad" style={{ ...celda, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                        {/* El completo no lleva conteo: sale entero. */}
                        {r.tipo === 'completo' ? <span style={{ color: c.texto3, fontWeight: 400 }}>entero</span> : r.cantidad}
                      </td>
                      {/* Lo que el pallet todavía puede dar: lo separado en
                          capachos sin entregar ya no está para otra orden. */}
                      <td className={lado} data-rotulo="Remanente" style={{ ...celda, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {r.tipo === 'completo' ? (
                          <span style={{ color: c.texto3 }}>—</span>
                        ) : (
                          <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.3 }}>
                            <span style={{ fontWeight: 600, color: r.remanente < r.cantidad ? c.ambar : c.texto }}>{r.remanente}</span>
                            {r.remanente < r.cantidad && (
                              <span style={{ fontSize: 11.5, color: c.texto3 }}>{r.cantidad - r.remanente} en capachos</span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className={lado} data-rotulo="Ubicación" style={celda}>
                        {!r.ubicado && !puedeUbicar ? (
                          <span style={{ fontSize: 12.5, color: c.ambar, fontWeight: 600 }}>{r.ubicacion}</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => (r.ubicado ? irAlMapa(r.ubicacion) : irAUbicar(r.id))}
                            title={r.ubicado ? `Abrir ${r.ubicacion} en el mapa del depósito` : `Ubicar ${r.id}`}
                            style={{
                              border: 'none',
                              background: 'none',
                              padding: 0,
                              font: 'inherit',
                              textAlign: 'left',
                              textDecoration: 'underline',
                              textUnderlineOffset: 2,
                              fontFamily: r.ubicado ? mono : undefined,
                              fontSize: 12.5,
                              color: r.ubicado ? c.texto2 : c.ambar,
                              fontWeight: r.ubicado ? 400 : 600,
                              cursor: 'pointer',
                            }}
                          >
                            {r.ubicado ? textoUbicacion(r.ubicacion, r.nivel) : r.ubicacion}
                          </button>
                        )}
                      </td>
                      {/* El pallet que sale entero no se fracciona: hay que
                          poder distinguirlo de un vistazo, sin abrirlo. El chip
                          además se toca: es el único lugar donde se le puede
                          cambiar la clase al pallet que entró por el importador
                          y nunca pasó por Armado. */}
                      <td className={`${lado} wms-acciones`} style={celda}>
                        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                          <EstadoChip estado={r.estado} />
                          <button
                            type="button"
                            onClick={() => void alternarTipo(r.id, r.tipo)}
                            disabled={cambiando === r.id}
                            title={
                              r.tipo === 'completo'
                                ? 'Sale entero. Tocá para que vuelva a fraccionarse en capachos.'
                                : 'Se fracciona en capachos. Tocá para marcarlo como que sale entero.'
                            }
                            style={chipBoton(r.tipo === 'completo')}
                          >
                            {r.tipo === 'completo' ? 'entero' : 'se fracciona'}
                          </button>
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>

          <Paginador
            pagina={pallets.pagina}
            paginas={pallets.paginas}
            total={pallets.total}
            desde={pallets.desde}
            que="pallets"
            onIr={pallets.setPagina}
          />

          {datos && delTipo.length === 0 && (
            <Vacio
              icono="search_off"
              titulo="Sin resultados"
              detalle="No encontramos pallets que coincidan con la búsqueda."
              accion={
                <button type="button" onClick={limpiar} style={botonSecundario}>
                  Limpiar filtros
                </button>
              }
            />
          )}
        </div>
      )}
      {dialogoConfirmar}
    </div>
  );
}
